import React from "react";
import type { EChartsCoreOption } from "echarts/core";
import type { ChartSettings } from "../constants";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { getChartSeriesColors } from "@/components/chart/styles/colors";

interface LegendItem {
  name: string;
  color: string;
  iconType:
    | "line"
    | "circle"
    | "rect"
    | "roundRect"
    | "triangle"
    | "diamond"
    | "pin"
    | "arrow"
    /** Thin vertical bar — Cash Flow module / ECharts path legend parity */
    | "verticalBar";
  percentageText?: string; // For pie/donut legends when enabled
  value?: number; // Raw value for formatting
  percentage?: number; // Percentage value (0-100) for formatting
}

interface CustomEChartsLegendProps {
  chartOption: EChartsCoreOption;
  settings?: Partial<ChartSettings>;
  containerWidth?: number;
  /** Theme for text color adaptation */
  theme?: "light" | "dark";
  /** Preloaded chart colors (prevents race condition in PDF rendering) */
  preloadedChartColors?: string[];
  /**
   * Report builder chart type — used to match Cash Flow charts (vertical bar legend markers,
   * exclude internal helper series). Native ECharts legend is hidden; this component drives the visible legend.
   */
  chartType?: string;
}

export const CustomEChartsLegend: React.FC<CustomEChartsLegendProps> = ({
  chartOption,
  settings = {},
  containerWidth = 800,
  theme = "light",
  preloadedChartColors,
  chartType,
}) => {
  // Ensure settings is always an object (handle explicit null/undefined)
  const safeSettings = settings ?? {};

  // Company/default palette fallback
  // Use preloaded colors if provided (PDF rendering), otherwise fetch independently
  const { chartColors: companyChartColors, isLoading: isLoadingColors } = useCompanyColors();
  const effectiveChartColors = preloadedChartColors ?? companyChartColors;
  const companyPalette = getChartSeriesColors(effectiveChartColors);

  // Extract legend items from chart option
  const legendItems = React.useMemo(() => {
    const series: any[] = (chartOption as any).series || [];
    const paletteFromOption: string[] = (chartOption as any).color || [];
    const paletteFromSettings: string[] =
      (safeSettings as any)?.colorPalette || [];
    const palette: string[] =
      paletteFromOption.length > 0
        ? paletteFromOption
        : paletteFromSettings && paletteFromSettings.length > 0
          ? paletteFromSettings
          : companyPalette;

    const resolveSeriesColor = (s: any, index: number): string => {
      const color = s?.itemStyle?.color ||
        s?.lineStyle?.color ||
        s?.areaStyle?.color ||
        s?.color ||
        palette[index % Math.max(palette.length || 1, 1)] ||
        "#000000";
      
      return color;
    };

    // If any pie series present, build legend from pie data items (slices)
    const hasPie = series.some((s) => s && s.type === "pie");
    if (hasPie) {
      const pieSeries = series.filter((s) => s && s.type === "pie");
      // Compute total across first pie series for percentages (common case is single series)
      const firstPie = pieSeries[0] || { data: [] };
      const total = (firstPie.data || []).reduce((sum: number, d: any) => {
        const v =
          typeof d?.value === "number" ? d.value : Number(d?.value) || 0;
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);

      const showPercentages = safeSettings.showLegendPercentages === true;

      const items: LegendItem[] = [];
      
      // Determine icon type based on settings
      let iconType: LegendItem["iconType"] = "circle";
      const userIcon = safeSettings.legendIcon || "auto";

      if (userIcon === "auto") {
        iconType = "circle"; // Default for pie/donut charts
      } else if (userIcon === "line") {
        iconType = "line";
      } else if (
        ["circle", "rect", "roundRect", "triangle", "diamond", "pin", "arrow"].includes(
          userIcon
        )
      ) {
        iconType = userIcon as LegendItem["iconType"];
      }
      
      pieSeries.forEach((s) => {
        const dataArr: any[] = Array.isArray(s.data) ? s.data : [];
        dataArr.forEach((d, idx) => {
          const name = d?.name ?? `Item ${idx + 1}`;
          // Resolve color precedence: data.itemStyle.color > series.itemStyle.color > palette[index]
          const color =
            d?.itemStyle?.color || d?.color || resolveSeriesColor(s, idx);

          const itemValue = Number(d?.value) || 0;
          
          // Skip zero-value items from legend (they clutter the display)
          if (itemValue === 0) {
            return;
          }
          
          const itemPercentage = total > 0 ? (itemValue / total) * 100 : 0;
          
          const percentageText =
            showPercentages && total > 0
              ? `${itemPercentage.toFixed(1)}%`
              : undefined;

          items.push({ name, color, iconType, percentageText, value: itemValue, percentage: itemPercentage });
        });
      });
      return items;
    }

    // Non-pie charts: build one item per series that has data
    // Filter out series with no data points to avoid showing extra legend entries
    const isCashFlowChart =
      chartType === "cashflow-projection" || chartType === "cashflow-fan";

    const seriesWithData = series.filter((s: any) => {
      if (!s || !s.data) return false;
      if (s.showInLegend === false) return false;
      if (s.type === "custom") return false;
      const rawName = String(s.name ?? "");
      if (
        rawName === "goalPeriodIndicators" ||
        rawName === "_goalPeriodIndicators"
      ) {
        return false;
      }
      // Check if series has at least one valid data point
      if (Array.isArray(s.data)) {
        return s.data.some((d: any) => d !== null && d !== undefined && (typeof d === "number" ? isFinite(d) : true));
      }
      // For scatter charts, data might be an array of [x, y] pairs
      if (Array.isArray(s.data) && s.data.length > 0) {
        return s.data.length > 0;
      }
      return false;
    });
    
    return seriesWithData.map((s: any, index: number) => {
      const name = s.name || `Series ${index + 1}`;
      const color = resolveSeriesColor(s, index);

      // Determine icon type based on series type and settings
      let iconType: LegendItem["iconType"] = "circle";
      const userIcon = safeSettings.legendIcon || "auto";

      if (userIcon === "auto") {
        if (isCashFlowChart && s.type === "bar") {
          iconType = "verticalBar";
        } else if (s.type === "line" || s.type === "area") {
          iconType = "line";
        } else if (s.type === "scatter") {
          iconType = "circle";
        } else if (s.type === "bar") {
          iconType = "rect";
        }
      } else if (userIcon === "line") {
        iconType = "line";
      } else if (
        ["circle", "rect", "roundRect", "triangle", "diamond", "pin", "arrow"].includes(
          userIcon
        )
      ) {
        iconType = userIcon as LegendItem["iconType"];
      }

      return { name, color, iconType } as LegendItem;
    });
  }, [
    chartOption,
    chartType,
    safeSettings.legendIcon,
    safeSettings.showLegendPercentages,
    companyPalette,
  ]);

  if (safeSettings.legend?.show === false) {
    return null;
  }

  // Determine layout based on legendPosition
  // When position is left/right, force vertical layout for the legend items
  const legendPosition = safeSettings.legendPosition || "bottom";
  const isHorizontalPosition = legendPosition === "left" || legendPosition === "right";
  
  // If legend is on left/right of chart, use vertical item layout regardless of legendLayout setting
  const effectiveLegendLayout = isHorizontalPosition ? "vertical" : (safeSettings.legendLayout || "horizontal");
  const alignment = safeSettings.legend?.left || "center";
  // Don't use bottom offset as top margin - it's meant for ECharts positioning
  const verticalOffset = 0;
  
  // Use new legendFontSize setting, fall back to legacy path, then default
  const fontSize = safeSettings.legendFontSize ?? safeSettings.legend?.textStyle?.fontSize ?? 10;
  // Use new legendItemGap setting, fall back to default
  const itemGap = safeSettings.legendItemGap ?? 8;
  // Use legendFontFamily setting, fall back to default sans
  const fontFamilyClass = safeSettings.legendFontFamily || "font-sans";
  
  // Helper to format legend label based on legendLabelFormat setting
  const formatLegendLabel = (item: LegendItem): string => {
    const labelFormat = safeSettings.legendLabelFormat || "name";
    const customFormat = safeSettings.legendCustomFormat || "{name}: {value}";
    
    switch (labelFormat) {
      case "name-value":
        return `${item.name}: ${item.value?.toLocaleString() ?? ""}`;
      case "name-percent":
        return `${item.name}: ${item.percentage?.toFixed(1) ?? "0"}%`;
      case "custom":
        // Replace placeholders in custom format
        return customFormat
          .replace("{name}", item.name)
          .replace("{value}", item.value?.toLocaleString() ?? "")
          .replace("{percent}", `${item.percentage?.toFixed(1) ?? "0"}%`);
      case "name":
      default:
        // For backward compatibility, also check showLegendPercentages
        if (item.percentageText) {
          return `${item.name}: ${item.percentageText}`;
        }
        return item.name;
    }
  };

  // Use theme-aware text color (light text for dark mode, dark text for light mode)
  // Priority: legendTextColor > legacy legend.textStyle.color > theme default
  const textColor =
    safeSettings.legendTextColor ||
    safeSettings.legend?.textStyle?.color ||
    (theme === "dark" ? "#e2e8f0" : "#1e293b");

  // Icon dimensions
  const firstIcon = legendItems[0]?.iconType;
  const iconWidth =
    safeSettings.legendIcon === "line" || firstIcon === "line"
      ? 20
      : firstIcon === "verticalBar"
        ? 4
        : 12;
  const iconHeight =
    safeSettings.legendIcon === "line" || firstIcon === "line"
      ? 4
      : firstIcon === "verticalBar"
        ? 12
        : 10;

  // Render icon based on type
  // Use consistent 10x10 container for all icons with viewBox for proper scaling and alignment
  const renderIcon = (item: LegendItem) => {
    const { color, iconType } = item;

    // Consistent container style for all icons - fixed width ensures alignment
    const iconContainerStyle: React.CSSProperties = {
      marginRight: 6,
      flexShrink: 0,
      width: 10,
      height: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    };

    if (iconType === "line") {
      return (
        <span style={iconContainerStyle}>
          <svg width={iconWidth} height={iconHeight} viewBox={`0 0 ${iconWidth} ${iconHeight}`}>
            <rect x={0} y={0} width={iconWidth} height={iconHeight} fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "verticalBar") {
      return (
        <span
          style={{
            ...iconContainerStyle,
            width: 10,
            height: 14,
            alignItems: "center",
          }}
        >
          <svg width={4} height={12} viewBox="0 0 4 12" aria-hidden>
            <rect x={0} y={0} width={4} height={12} rx={0.5} fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "circle") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <circle cx={4} cy={4} r={3} fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "rect") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <rect x={1} y={1} width={6} height={6} fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "roundRect") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <rect x={1} y={1} width={6} height={6} rx={1} fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "triangle") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <polygon points="4,1 7,7 1,7" fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "diamond") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={8} viewBox="0 0 8 8">
            <polygon points="4,1 7,4 4,7 1,4" fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "pin") {
      return (
        <span style={iconContainerStyle}>
          <svg width={8} height={10} viewBox="0 0 8 10">
            <circle cx={4} cy={3} r={2.5} fill={color} />
            <polygon points="4,10 1.5,4.5 6.5,4.5" fill={color} />
          </svg>
        </span>
      );
    }

    if (iconType === "arrow") {
      return (
        <span style={iconContainerStyle}>
          <svg width={10} height={8} viewBox="0 0 10 8">
            <polygon points="0,2 6,2 6,0 10,4 6,8 6,6 0,6" fill={color} />
          </svg>
        </span>
      );
    }

    // Default to circle
    return (
      <span style={iconContainerStyle}>
        <svg width={8} height={8} viewBox="0 0 8 8">
          <circle cx={4} cy={4} r={3} fill={color} />
        </svg>
      </span>
    );
  };

  // Vertical layout (for left/right legend positions)
  if (effectiveLegendLayout === "vertical") {
    return (
      <div
        className={fontFamilyClass}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: `${itemGap}px`,
          fontSize: `${fontSize}px`,
          color: textColor,
          background: "transparent",
        }}
      >
        {legendItems.map((item, index) => (
          <div
            key={`legend-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {renderIcon(item)}
            <span
              style={{
                color: textColor,
                wordBreak: "break-word",
                lineHeight: "1.3",
                whiteSpace: "nowrap",
              }}
            >
              {formatLegendLabel(item)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Horizontal layout
  if (effectiveLegendLayout === "horizontal") {
    const justifyContent =
      alignment === "left"
        ? "flex-start"
        : alignment === "right"
          ? "flex-end"
          : "center";

    return (
      <div
        className={fontFamilyClass}
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent,
          alignItems: "center",
          gap: `${itemGap}px`,
          marginTop: `${verticalOffset}px`,
          width: "100%",
          fontSize: `${fontSize}px`,
          color: textColor,
          background: "transparent",
        }}
      >
        {legendItems.map((item, index) => (
          <div
            key={`legend-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              maxWidth: "400px",
              minWidth: 0,
            }}
          >
            {renderIcon(item)}
            <span
              style={{
                color: textColor,
                wordBreak: "break-word",
                lineHeight: "1.3",
                flex: 1,
                minWidth: 0,
              }}
            >
              {formatLegendLabel(item)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Grid layout
  const gridColumns = safeSettings.legendColumns || "auto";
  const gridWidth = safeSettings.legendWidth || 80;

  let calculatedColumns: number;
  if (gridColumns === "auto") {
    calculatedColumns =
      legendItems.length <= 3 ? Math.max(1, legendItems.length) : 2;
  } else {
    calculatedColumns = Number(gridColumns);
  }

  calculatedColumns = Math.min(
    calculatedColumns,
    Math.max(1, legendItems.length),
    2
  );

  // Distribute items more evenly across columns
  const columns: LegendItem[][] = [];
  const baseItemsPerColumn = Math.floor(legendItems.length / calculatedColumns);
  const extraItems = legendItems.length % calculatedColumns;

  let currentIndex = 0;
  for (let col = 0; col < calculatedColumns; col++) {
    // Give extra items to later columns for better visual balance
    const itemsInThisColumn =
      col >= calculatedColumns - extraItems
        ? baseItemsPerColumn + 1
        : baseItemsPerColumn;

    columns.push(
      legendItems.slice(currentIndex, currentIndex + itemsInThisColumn)
    );
    currentIndex += itemsInThisColumn;
  }

  // Check if we should center the grid (when last column has fewer items)
  const shouldCenterGrid =
    calculatedColumns > 1 &&
    columns.length > 1 &&
    columns[columns.length - 1].length < columns[0].length;

  // Compute justifyContent for the outer flex wrapper based on alignment
  const outerJustifyContent =
    alignment === "left"
      ? "flex-start"
      : alignment === "right"
        ? "flex-end"
        : "center";

  return (
    <div
      className={fontFamilyClass}
      style={{
        position: "relative",
        width: "100%",
        marginTop: `${verticalOffset}px`,
        fontSize: `${fontSize}px`,
        color: textColor,
        background: "transparent",
        display: "flex",
        justifyContent: outerJustifyContent,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${calculatedColumns}, 1fr)`,
          gap: "24px",
          width: `${gridWidth}%`,
        }}
      >
        {columns.map((columnItems, colIndex) => (
          <div
            key={`column-${colIndex}`}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1px",
              minWidth: 0,
              alignItems: outerJustifyContent === "center" ? "center" : outerJustifyContent === "flex-end" ? "flex-end" : "flex-start",
            }}
          >
            {columnItems.map((item, itemIndex) => (
              <div
                key={`legend-${colIndex}-${itemIndex}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0, // Allow flex item to shrink below content size
                }}
              >
                {renderIcon(item)}
                <span
                  style={{
                    color: textColor,
                    wordBreak: "break-word",
                    lineHeight: "1.3",
                    flex: 1,
                    minWidth: 0, // Allow text to wrap instead of overflow
                  }}
                >
                  {formatLegendLabel(item)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
